/**
 * Token accounting shared by the provider runtime and the orchestration log.
 *
 * Its own module because both `providerRuntime` (which reports a turn's usage)
 * and `orchestration` (which persists it) need the same shape, and
 * `providerRuntime` already imports `orchestration` — putting it in either of
 * them would close an import cycle between two modules whose top level builds
 * schemas eagerly.
 */
import { Schema } from "effect";

import { NonNegativeInt, TrimmedNonEmptyString } from "./baseSchemas";

/**
 * What one model consumed during a single turn, normalized across providers so
 * the same row can be persisted and aggregated regardless of who produced it.
 *
 * The counters follow Codex's shape, which is the narrower of the two, so that
 * no provider needs a lossy conversion:
 * - `inputTokens` counts EVERY input token, cached and cache-write included.
 * - `cachedInputTokens` and `cacheCreationInputTokens` are SUBSETS of
 *   `inputTokens`; adding them to it double-counts.
 * - `reasoningOutputTokens` is a SUBSET of `outputTokens`.
 * - `totalTokens` is `inputTokens + outputTokens`.
 *
 * `costUsd` is only present when the provider itself reports a price. We never
 * derive it from a rate table, so Codex rows normally have no cost at all.
 */
/**
 * What the tokens were spent on.
 *
 * Not every model call is a turn of a conversation. The server also writes
 * commit messages, PR text, branch names and thread titles on its own behalf,
 * and those calls used to be invisible in the reports — folded into nothing,
 * because nothing measured them. Reporting them under `"chat"` would be worse
 * than not reporting them: a thread's total would silently include work no user
 * asked for, and the per-user rankings would move for reasons nobody could
 * explain. So the ledger carries the reason next to the numbers.
 *
 * `"chat"` is the default, and every row that predates this axis is one: the
 * migration backfills it, so an existing installation's reports read exactly as
 * they did before, with a single bucket.
 *
 * `"thread-context"` has no writer yet — it belongs to the `read_thread_context`
 * tool. It is in the union from the start so the reports, the filter and the
 * stored values never need a second migration to accept it.
 */
export const TurnUsageKind = Schema.Literals([
  "chat",
  "thread-title",
  "commit-message",
  "pr-content",
  "branch-name",
  "thread-context",
  "thread-recap",
]);
export type TurnUsageKind = typeof TurnUsageKind.Type;

/** Enumerable form, for the report's filter dropdown. Same order as above. */
export const TURN_USAGE_KIND_VALUES = [
  "chat",
  "thread-title",
  "commit-message",
  "pr-content",
  "branch-name",
  "thread-context",
  "thread-recap",
] as const;

/** The value assumed for a row that does not say — see `TurnUsageKind`. */
export const DEFAULT_TURN_USAGE_KIND: TurnUsageKind = "chat";

export const TurnModelUsage = Schema.Struct({
  model: TrimmedNonEmptyString,
  inputTokens: NonNegativeInt,
  cachedInputTokens: NonNegativeInt,
  cacheCreationInputTokens: NonNegativeInt,
  outputTokens: NonNegativeInt,
  reasoningOutputTokens: NonNegativeInt,
  totalTokens: NonNegativeInt,
  costUsd: Schema.optional(Schema.Number),
});
export type TurnModelUsage = typeof TurnModelUsage.Type;
